import React, { useMemo, useState } from "react";
import { Copy, Check, Link2, QrCode } from "lucide-react";
import { toast } from "sonner";
import { AdminShell } from "./AdminShell";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const SOURCES = ["whatsapp", "instagram", "facebook", "linkedin", "email", "qr", "referral"];

function LinkRow({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
      <Link2 size={15} className="text-slate-400 shrink-0 ml-1" />
      <span className="flex-1 text-xs text-slate-600 truncate" data-testid="campaign-link">{url}</span>
      <button data-testid="copy-link" onClick={copy} className="rounded-md bg-navy text-white px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 shrink-0">
        {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export default function AdminCampaigns() {
  const origin = window.location.origin;
  const [source, setSource] = useState("whatsapp");
  const [campaign, setCampaign] = useState("");
  const [medium, setMedium] = useState("");

  const build = (src) => {
    const p = new URLSearchParams();
    p.set("utm_source", src);
    if (medium.trim()) p.set("utm_medium", medium.trim());
    if (campaign.trim()) p.set("utm_campaign", campaign.trim());
    return `${origin}/?${p.toString()}`;
  };

  const customUrl = useMemo(() => build(source), [origin, source, campaign, medium]);
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(customUrl)}`;

  return (
    <AdminShell title="Campaign Links">
      <div className="max-w-3xl space-y-6">
        <div className="rounded-2xl bg-white border border-slate-200 p-6 card-shadow">
          <div className="font-heading font-bold text-navy mb-1">Build a Campaign Link</div>
          <p className="text-xs text-slate-500 mb-5">Share these links on WhatsApp, Instagram, email or a QR code. Any lead that comes through is automatically tagged with its source and campaign, so you can see which channel performs best.</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Source</Label>
              <select data-testid="campaign-source" value={source} onChange={(e) => setSource(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-200 h-10 px-3 text-sm bg-white capitalize">
                {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Campaign (optional)</Label>
              <Input data-testid="campaign-name" value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="e.g. june_launch" className="mt-1.5 rounded-lg" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Medium (optional)</Label>
              <Input data-testid="campaign-medium" value={medium} onChange={(e) => setMedium(e.target.value)} placeholder="e.g. story, post, dm" className="mt-1.5 rounded-lg" />
            </div>
          </div>
          <div className="mt-5">
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Your Link</Label>
            <LinkRow url={customUrl} />
          </div>
          <div className="mt-5 flex items-center gap-4">
            <img src={qrSrc} alt="QR code" data-testid="campaign-qr" className="rounded-xl border border-slate-200" width={120} height={120} />
            <div className="text-xs text-slate-500">
              <div className="font-semibold text-navy flex items-center gap-1.5 mb-1"><QrCode size={14} /> QR Code</div>
              Print this on flyers or show it in person. Scanning opens the check-up with this campaign attached.
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-6 card-shadow">
          <div className="font-heading font-bold text-navy mb-4">Quick Links by Channel</div>
          <div className="space-y-3">
            {SOURCES.map((s) => (
              <div key={s}>
                <div className="text-xs font-semibold text-slate-500 capitalize mb-1">{s}</div>
                <LinkRow url={build(s)} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
